import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { cx } from 'linaria';
import { counterSticky } from 'common/components/product-price-view/sticky.styles';
import {
  getDisplayedName,
  getProductImages,
  getProductId,
  getPrimaryPrice,
  isWebSellable,
} from 'features/product-info/ducks/selectors';
import { PdpMeasurementHint } from 'features/product-info/components/measurement-hint';
import { ImagePlaceholder } from '@navi-toolbox/ui-components';
import { dataIsLoadingCommon, counter, prices } from '../pdp-showcase/styles';
import { PdpPriceView } from '../pdp-price-view';
import { PdpAddToBasket } from '../pdp-add-to-basket';
import { mediaMobile } from '../pdp-media-gallery/styles';
import { PdpSelectTintColor } from '../pdp-select-tint-color';
import { useOptimisedImages } from '@navi-toolbox/hooks';
import { getYaSplitInfo } from 'features/page-model';
import { getCartItem } from 'features/basket';
import {
  stickyCardWrapper,
  innerContainer,
  innerContainerFixed,
  thumbImg,
  Basket,
  ToCart,
  unfixedOuterContainer,
  fixedOuterContainer,
  hidden,
} from './pdp-sticky-card-panel.styles';
import { PriceAndAddToBasketProps } from './types';
import { useTranslation } from 'react-i18next';
import { StickyCardPanelSkeleton } from './sticky-card-panel-skeleton';
import { YandexSplitBanner } from 'features/yandex-split-banner';
import { useAddBodyPadding, useStickyLogic } from './hooks';
import { getIsSetPath } from 'common/selectors';
import { DATA_QA_FIXED_OUTER_CONTAINER } from './constants';
import { getDeviceType } from 'common/selectors/device-type';

export const StickyCardPanel: React.FC<PriceAndAddToBasketProps> = ({
  productDataIsLoading,
  shouldShowTintService,
  shouldShowPrice,
  isMeasurementHint,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuMobileRef = useRef<HTMLDivElement>(null);
  const photoSrc = useSelector(getProductImages)?.[0].url ?? '';
  const productName = useSelector(getDisplayedName) ?? '';
  const { img } = useOptimisedImages({
    src: photoSrc,
    sizes: {
      img: {
        w: '40',
        h: '40',
      },
    },
  });
  const isSellable = useSelector(isWebSellable);
  const productId = useSelector(getProductId);
  const { price } = useSelector(getPrimaryPrice) || {};
  const cartEntity = useSelector(getCartItem(productId || ''));
  const inBasket = !!cartEntity;
  const quantity = cartEntity?.quantity || 0;
  const yaSplitInfo = useSelector(getYaSplitInfo);
  const isSet = useSelector(getIsSetPath);
  const deviceType = useSelector(getDeviceType);

  const { isSticky } = useStickyLogic(containerRef);
  useAddBodyPadding(isSet, deviceType !== 'desktop');
  const visible = isSticky && !productDataIsLoading;

  return (
    <div className={stickyCardWrapper}>
      <div className={unfixedOuterContainer} ref={containerRef}>
        {productDataIsLoading ? (
          <StickyCardPanelSkeleton />
        ) : (
          <>
            {isSellable && shouldShowPrice && (
              <div className={innerContainer}>
                {shouldShowTintService && <PdpSelectTintColor />}
                <div data-qa="prices_mf-pdp" className={prices}>
                  <PdpPriceView
                    isStickyPanel={false}
                    className={cx(productDataIsLoading && dataIsLoadingCommon)}
                    inBasket={inBasket}
                    quantity={quantity}
                  />
                </div>
                <ToCart data-qa="to-cart_mf-pdp">
                  <PdpAddToBasket
                    size="l"
                    classnameCounter={counter}
                    isStickyPanel={false}
                    className={cx(productDataIsLoading && dataIsLoadingCommon)}
                  />
                </ToCart>
              </div>
            )}
          </>
        )}
      </div>
      {isMeasurementHint && <PdpMeasurementHint />}
      {shouldShowPrice && yaSplitInfo && (
        <YandexSplitBanner yaSplitInfo={yaSplitInfo} price={price} quantity={quantity} />
      )}
      <div
        data-qa={DATA_QA_FIXED_OUTER_CONTAINER}
        ref={menuMobileRef}
        className={visible ? fixedOuterContainer : cx(fixedOuterContainer, hidden)}
      >
        <div data-qa="photo_thumbnail_mf-pdp" className={thumbImg}>
          <picture data-qa="picture-thumbnail-sticky-element">
            <source media={mediaMobile} srcSet={`${img?.xx} 2x, ${img?.x} 1x`} />
            <ImagePlaceholder
              src={img?.x || ''}
              srcSet={`${img?.xx} 2x, ${img?.x} 1x`}
              alt={productName}
              title={productName}
              className={thumbImg}
              loading="lazy"
              fallbackWidth={40}
              fallbackHeight={40}
              fallbackAlt={t('ui.image.alt')}
            />
          </picture>
        </div>
        <div className={cx(innerContainer, innerContainerFixed)}>
          <div data-qa="prices_mf-pdp-sticky">
            <PdpPriceView
              isStickyPanel={true}
              className={cx(prices, productDataIsLoading && dataIsLoadingCommon)}
              inBasket={inBasket}
              quantity={quantity}
            />
          </div>
          <Basket additionalMargin={true}>
            <ToCart data-qa="to-cart_mf-pdp-sticky">
              <PdpAddToBasket
                size="l"
                classnameCounter={counterSticky}
                isStickyPanel={true}
                className={cx(productDataIsLoading && dataIsLoadingCommon)}
              />
            </ToCart>
          </Basket>
        </div>
      </div>
    </div>
  );
};
